import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import CopyAllOutlined from "@mui/icons-material/CopyAllOutlined";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  MenuList,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import * as React from "react";
import {
  BooleanInput,
  CheckboxGroupInput,
  DateInput,
  Edit,
  ImageField,
  Link,
  required,
  SelectInput,
  Show,
  ShowContextProvider,
  ShowView,
  SimpleForm,
  SimpleShowLayout,
  TextInput,
  Toolbar,
  useRecordContext,
  useRefresh,
  useShowController,
} from "react-admin";
import { useFormContext } from "react-hook-form";
import sendRequest from "../dataProvider/sendRequest";
import SectionStyled from "../products/groups/SectionStyled";
import CustomTabButton from "../unit/CustomTabButton";
import HeaderLabelGridField from "../unit/HeaderLabelGridField";
import HmSubHeaderTitle from "../unit/HmSubHeaderTitle";
import HmTextInput from "../unit/HmTextInput";
import formatPhone from "../unit/inputFormat/formatPhone";
import parsePhone from "../unit/inputFormat/parsePhone";
import OneZeroInput from "../unit/OneZeroInput";
import UploadImage from "../unit/UploadImage";
import useNotify from "../unit/useNotify";
import CustomAsideCard from "../unit/CustomAsideCard";

const MUI_X_PRODUCTS: any[] = [
  {
    id: "linkto_profile", //done
    label: "Profile",
    children: [],
  },
  {
    id: "linkto_welcome",
    label: "Welcome Page",
    children: [],
  },
  {
    id: "linkto_language",
    label: "Language Settings",
    children: [],
  },
  {
    id: "linkto_contact",
    label: "Customer Contact",
    children: [],
  },
  {
    id: "linkto_esign",
    label: "E-Signature",
    children: [],
  },
  {
    id: "linkto_logos",
    label: "Logos",
    children: [],
  },

  {
    id: "linkto_probation",
    label: "Access",
    children: [],
  },
  {
    id: "linkto_alerts",
    label: "Alerts",
    children: [],
  },
];
const ShowEsignChangeEmailMessage = () => {
  const record = useRecordContext();
  if (record?.sNewEmail && record?.sNewEmail != record?.esign_email) {
    return (
      <Typography variant="caption" color="error">
        Email has been changed to{" "}
        <span style={{ fontWeight: "bold" }}>{record?.sNewEmail}</span>, waiting
        for confirmation.
      </Typography>
    );
  }
};
const ShowLogos = ({ ...props }) => {
  const size = props?.size || "large";
  const record = useRecordContext();
  const refresh = useRefresh();
  const [filelist, setFilelist] = React.useState<any[]>([]);
  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: "10px", mb: "20px" }}
    >
      {size == "small" && (
        <Typography variant="subtitle1">
          <b>Square logo</b> (as brand image shown on the top banner of customer
          site. Tip: Use logo for Twitter account)
        </Typography>
      )}
      {size != "small" && (
        <Typography variant="subtitle1">
          <b>Rectangular logo</b> (with institution name for Welcome Page and
          legal documents. Tip: Use full logo)
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          mb: "20px",
          "& .RaImageField-image": {
            height: "100px !important",
            width: "auto !important",
            objectFit: "contain",
          },
          "& > div": {
            height: "80px",
            display: "flex",
            alignItems: "center",
          },
        }}
      >
        {size == "small" ? (
          <>
            <Box sx={{ width: "100%", mt: 1 }}>
              {record?.sBankBcLogo && (
                <ImageField
                  source="sBankBcLogo"
                  title="Logo"
                  sx={{
                    "& img": {
                      maxWidth: "100%",
                    },
                  }}
                />
              )}
            </Box>
            <Box sx={{ width: "150px", mt: 1 }}>
              <UploadImage
                uploadName="sBankBcLogo"
                uploadApi={"banks/actions/uploadLogo/" + record?.id}
                onSuccess={(filelist: any) => {
                  refresh();
                }}
              />
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ width: "100%", mt: 1 }}>
              {record?.sBankLogo && (
                <ImageField
                  source="sBankLogo"
                  title="Logo"
                  sx={{
                    "& img": {
                      maxWidth: "100%",
                    },
                  }}
                />
              )}
            </Box>
            <Box sx={{ width: "150px", mt: 1 }}>
              <UploadImage
                uploadName="sBankLogo"
                uploadApi={"banks/actions/uploadLogo/" + record?.id}
                onSuccess={(filelist: any) => {
                  refresh();
                }}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
const CustomLinkField = ({ ...props }) => {
  const record = useRecordContext();
  const [link, setLink] = React.useState(record?.[props?.source] || "");
  const { success, error } = useNotify();

  const handleCopy = () => {
    navigator.clipboard.writeText(link);
    success("Link copied to clipboard");
  };
  return (
    <TextInput
      source={props?.source}
      label={props?.label}
      name={props?.name}
      variant="outlined"
      value={link}
      sx={{
        width: "100%",
        "& input.Mui-readOnly": {
          // backgroundColor: "#ddd",
          // borderRadius: "10px",
          color: "#00000061",
        },
      }}
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label={"Copy URL"}
              onClick={handleCopy}
              edge="end"
              sx={{ marginLeft: "0px" }}
            >
              <CopyAllOutlined />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

const CustomLanguageSettings = () => {
  const record = useRecordContext();

  return (
    <CheckboxGroupInput
      source="sLanguage"
      label="Language Settings"
      defaultValue={record?.sLanguage || ["en"]}
      row={false}
      fullWidth
      validate={[
        (value) => {
          if (value?.includes("en")) {
            return undefined;
          }
          return "English is required";
        },
      ]}
      choices={[
        { id: "en", name: "English", value: "en", required: true },
        { id: "es", name: "Spanish", value: "es" },
        { id: "ps", name: "Pashto", value: "ps" },
      ]}
      variant="outlined"
    />
  );
};
const CustomBankNameField = () => {
  const record = useRecordContext();
  return (
    <HmSubHeaderTitle
      title={`${record?.sBankName}`}
      sx={{
        fontSize: "1rem",
        ml: 1,
        mt: 0.5,
      }}
    />
  );
};

const BankShow = ({ ...props }) => {
  const record = useRecordContext();
  const [editEsignEmail, setEditEsignEmail] = React.useState(false);
  const { success, error } = useNotify();
  const id = record?.id;
  const [activeTab, setActiveTab] = React.useState(0);
  const handleChange = (newValue: number) => {
    setActiveTab(newValue);
  };

  const [rightTabs, setRightTabs] = React.useState<any[]>([]);
  const [selectedItem, setSelectedItem] = React.useState("linkto_profile");
  const [selectSubItem, setSelectSubItem] = React.useState("linkto_profile");
  // const bId = useGetRecordId();
  const scrollToSection = (sectionId: string) => {
    setSelectedItem(sectionId);
    setActiveTab(0);
    // setSelectSubItem("linkto_" + rightTabs[0]?.id);
  };
  const controllerProps = useShowController();

  const CustomAddress = ({ ...props }) => {
    const { record } = props;
    return (
      <Grid
        container
        spacing={2}
        sx={{
          mt: "0px",
          pt: "0px",
          "& .MuiGrid-item": { mt: "0px", pt: "0px" },
        }}
      >
        <Grid item xs={12} sm={6} sx={{ mt: "0px", pt: "0px" }}>
          <HmTextInput
            source="sStreet"
            name="street"
            label="Street"
            validate={[required()]}
          />
        </Grid>
        <Grid item xs={12} sm={6} sx={{ mt: "0px", pt: "0px" }}>
          <HmTextInput source="sStreet2" name="street2" label="Street 2" />
        </Grid>
        <Grid item xs={12} sm={6} sx={{ mt: "0px", pt: "0px" }}>
          <HmTextInput
            source="sCity"
            name="city"
            label="City"
            validate={[required()]}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <HmTextInput
            source="sState"
            name="state"
            label="State"
            validate={[required()]}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <HmTextInput
            source="sZip"
            name="zip_code"
            label="Zip Code"
            validate={[required()]}
            className="numericText"
          />
        </Grid>
      </Grid>
    );
  };

  const handleChangeMenu = (itemId: string) => {
    // console.log("handleChangeMenu itemId xxxxx:", itemId, activeTab);
    let defaultSubItem = "linkto_english";
    if (itemId == "Description") {
      defaultSubItem = "linkto_english";
    } else if (itemId == "Attributes") {
      defaultSubItem = "linkto_loanterms";
    } else if (itemId == "Application") {
      defaultSubItem = "linkto_community";
    } else if (itemId == "Review") {
      defaultSubItem = "linkto_process";
    } else if (itemId == "Servicing") {
      defaultSubItem = "linkto_servicing_disbursement";
    } else if (itemId == "Legal & Compliance") {
      defaultSubItem = "linkto_authorization_application";
    } else if (itemId == "Marketing") {
      defaultSubItem = "linkto_landing";
    }
    setSelectSubItem(defaultSubItem);
    setActiveTab(0);
  };
  const admin = localStorage.getItem("authrole") == "admin" ? true : false;
  type BankEditFormFields = {
    institution_name?: string;
    organization_type?: string;
    business_type?: string;
    bussiness_name?: string;
    ein?: string;
    street?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    bankUrl?: string;
    bank_phone?: string;
    bank_email?: string;
    contact_name?: string;
    textMessageNumber?: string;
    textMessageInstruction?: string;
    esign_firstname?: string;
    esign_lastname?: string;
    esign_email?: string;
    bankSolgan?: string;
    welcomeMsg?: string;
    language_settings?: string[];
    // Add other fields as needed
  };
  const SaveFormButton = ({ ...props }) => {
    const label = props?.label || "Save";
    const form = useFormContext();
    const id = form?.getValues("id");
    const { isDirty } = form.formState;
    const saveData = () => {
      const formData = form.getValues();

      let saveFormData = {};
      if (selectedItem === "linkto_profile") {
        saveFormData = {
          institution_name: formData.sBankName,
          organization_type: formData.sOrganizationType,
          business_type: formData.sBussinessType,
          business_name: formData.sBussinessClassification,
          ein: formData.sEin,
          street: formData.sStreet,
          street2: formData.sStreet2,
          city: formData.sCity,
          state: formData.sState,
          zip: formData.sZip,
          bankUrl: formData.sUrl,
          saveType: "info",
        };
        //上面这些不能为空，必填
        if (!saveFormData?.organization_type) {
          error("Organization Type is required");
          return;
        }
        if (!saveFormData?.business_type) {
          error("Business Type is required");
          return;
        }
        if (!saveFormData?.business_name) {
          error("Business Name is required");
          return;
        }
        if (!saveFormData?.ein) {
          error("EIN is required");
          return;
        }
        if (!saveFormData?.street) {
          error("Street is required");
          return;
        }
        if (!saveFormData?.city) {
          error("City is required");
          return;
        }
        if (!saveFormData?.state) {
          error("State is required");
          return;
        }
        if (!saveFormData?.zip) {
          error("Zip Code is required");
          return;
        }
        if (!saveFormData?.bankUrl) {
          error("Institution Website is required");
          return;
        }
      } else if (selectedItem === "linkto_contact") {
        saveFormData = {
          phone: formData.sBankPhone,
          bank_email: formData.sBankSupport,
          contactName: formData.sBankContactName,
          textMessageNumber: formData.textMessageNumber,
          textMessageInstruction: formData.textMessageInstruction,
          saveType: "contact",
        };
        if (!saveFormData?.phone) {
          error("Contact Phone Number is required");
          return;
        }
        if (!saveFormData?.bank_email) {
          error("Contact Email Address is required");
          return;
        }
        if (!saveFormData?.contactName) {
          error("Contact Name is required");
          return;
        }
      } else if (selectedItem === "linkto_esign") {
        saveFormData = {
          esign_firstname: formData.esign_firstname,
          esign_lastname: formData.esign_lastname,
          esign_email: formData.esign_email,
          saveType: "esign",
        };
        if (!saveFormData?.esign_firstname) {
          error("E-Signature Sender First Name is required");
          return;
        }
        if (!saveFormData?.esign_lastname) {
          error("E-Signature Sender Last Name is required");
          return;
        }
        if (!saveFormData?.esign_email) {
          error("E-Signature Sender Email is required");
          return;
        }
      } else if (selectedItem === "linkto_welcome") {
        saveFormData = {
          bankSolgan: formData.sSlogan,
          welcomeMsg: formData.sWelcomeMsg,
          saveType: "welcome",
        };
        if (!saveFormData?.bankSolgan) {
          error("Welcome Headline is required");
          return;
        }
        if (!saveFormData?.welcomeMsg) {
          error("Welcome Message is required");
          return;
        }
      } else if (selectedItem === "linkto_logos") {
        // console.log("record?.sBankBcLogo:", record?.sBankBcLogo);
        if (!formData.sBankBcLogo) {
          error("Small logo is required");
          return;
        }
        if (!formData.sBankBcLogo) {
          error("Large logo is required");
          return;
        }
        saveFormData = {
          sBankBcLogo: formData.sBankBcLogo,
          sBankLogo: formData.sBankLogo,
          saveType: "logos",
        };
      } else if (selectedItem === "linkto_language") {
        //查看是否含有en，en为必填
        if (!formData.sLanguage || !formData.sLanguage.includes("en")) {
          error("English is required in Language Settings");
          return;
        }
        saveFormData = {
          language_settings: formData.sLanguage,
          saveType: "language",
        };
      } else if (selectedItem === "linkto_probation") {
        saveFormData = {
          iProbation: formData.iProbation,
          dProbationStartTime: formData.dProbationStartTime,
          dProbationEndTime: formData.dProbationEndTime,
          saveType: "probation",
        };
      } else {
        saveFormData = {};
      }

      console.log("saveFormData:", saveFormData);
      const reqUrl = "banks/actions/edit";
      if (saveFormData) {
        sendRequest(reqUrl, { id: id, data: saveFormData }).then((res) => {
          if (res?.code == 200) {
            success("Saved successfully");
          } else {
            error("Error saving " + res?.message);
          }
        });
      } else {
        error("Error saving description: No data to save");
      }
    };
    if (selectedItem == "linkto_logos") {
      return null;
    }
    return (
      <>
        <Button
          variant="contained"
          color="primary"
          onClick={saveData}
          disabled={!isDirty}
          sx={{
            width: "120px",
          }}
        >
          {label}
        </Button>
      </>
    );
  };
  const CustomToolbar = (props: any) => (
    <Toolbar
      {...props}
      sx={{ display: "flex", justifyContent: "right", background: "#fff" }}
    >
      <div>
        <SaveFormButton label="Save" />
      </div>
    </Toolbar>
  );

  const handleChangeEsignEmail = () => {};
  const ProbationDateField = ({ ...props }) => {
    const record = useRecordContext();
    const [setProbation, setSetProbation] = React.useState(
      record?.iProbation == 1 ? 1 : 0
    );

    if (admin) {
      return (
        <Grid
          container
          spacing={2}
          sx={{
            mt: "0px",
            pt: "0px",
            "& .MuiGrid-item": { mt: "0px", pt: "0px" },
          }}
        >
          <Grid item xs={12} sm={12}>
            <OneZeroInput
              source="iProbation"
              label="Is Probation?"
              defaultValue={record?.iProbation == 1 ? true : false}
              onChange={(e) => {
                setSetProbation(e.target.value);
              }}
            />
          </Grid>

          {setProbation == 1 && (
            <>
              <Grid item xs={12} sm={6}>
                <DateInput
                  source="dProbationStartTime"
                  label="Probation Start Date"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateInput
                  source="dProbationEndTime"
                  label="Probation End Date"
                  variant="outlined"
                  onChange={(event: any) => {
                    const value = event.target.value;
                    if (value) {
                      const startDate = new Date(
                        document.querySelector(
                          'input[name="dProbationStartTime"]'
                        )?.value
                      );
                      const endDate = new Date(value);
                      if (endDate < startDate) {
                        error("Probation End Date cannot be before Start Date");
                      }
                    }
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>
      );
    } else {
      if (record?.iProbation == 1) {
        return (
          <Grid container sx={{ mt: "10px" }}>
            <HeaderLabelGridField
              title="Your institution is currently under probation"
              value=""
              dxs={1}
              tdxs={11}
            />
            <HeaderLabelGridField
              title="Probation start date"
              value={
                record?.dProbationStartTime
                  ? format(new Date(record?.dProbationStartTime), "MM/dd/yyyy")
                  : ""
              }
              dxs={10}
              tdxs={2}
            />
            <HeaderLabelGridField
              title="Probation end date"
              value={
                record?.dProbationEndTime
                  ? format(new Date(record?.dProbationEndTime), "MM/dd/yyyy")
                  : ""
              }
              dxs={10}
              tdxs={2}
            />
          </Grid>
        );
      } else {
        return <>This institution has normal status</>;
      }
    }
  };

  return (
    <>
      <div className="edit-container">
        <div className="aside-left">
          <BankListAside selected="banks" />
        </div>
        <div
          className="main-content"
          style={{
            order: 2,
            flexGrow: 1,
            marginTop: "48px",
            position: "relative",
          }}
        >
          <Show>
            <SimpleShowLayout sx={{ p: 0, border: "none" }}>
              <Box
                sx={{
                  mb: 0,
                  height: `calc(100vh - 190px)`,
                  width: `calc(100vw - 409px)`,
                  maxWidth: `calc(100vw - 409px)`,
                  borderRadius: "0px",
                  overflowY: "auto",
                  // background: "#6fb5b5ff",
                  border: "1px solid #e0e0e3",
                }}
              >
                <Box sx={{ borderBottom: 1, borderColor: "#00ccb1" }}>
                  {MUI_X_PRODUCTS.map((item, index) => {
                    if (item.id == selectedItem) {
                      return (
                        <CustomTabButton
                          key={item.id}
                          variant="text"
                          className={"Mui-selected"}
                          sx={{ minWidth: "150px", width: "150px" }}
                        >
                          {item.label}
                        </CustomTabButton>
                      );
                    } else {
                      return (
                        <CustomTabButton
                          key={item.id}
                          variant="text"
                          sx={{ minWidth: "150px", width: "150px" }}
                          onClick={() => {
                            scrollToSection(item.id);
                          }}
                        >
                          {item.label}
                        </CustomTabButton>
                      );
                    }
                  })}
                  {admin && (
                    <Link to="/banks">
                      <Tooltip
                        title="Return to Institutions List"
                        placement="left"
                      >
                        <ArrowCircleLeftIcon
                          color="primary"
                          sx={{ float: "right", mt: 1, mr: 1 }}
                        />
                      </Tooltip>
                    </Link>
                  )}
                </Box>
                <Box
                  sx={{
                    display: "block",
                    mt: "8px",
                    borderRadius: "0px",
                    padding: "8px",
                    paddingTop: "0px",
                    // backgroundColor: "#fff",
                    // backgroundColor: "pink",
                    // height: "calc(100vh - 145px)",
                  }}
                >
                  <SimpleForm toolbar={<CustomToolbar />}>
                    <SectionStyled
                      id={"section_bankedit"}
                      sx={{
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          padding: "10px",
                          paddingTop: "0px",
                          // height: "calc(100vh - 285px)",
                          // height: "100%",
                          // overflowY: "auto",
                          // background: "yellow",
                        }}
                      >
                        {/* hidden button */}
                        <input
                          type="hidden"
                          name="saveAction"
                          value={selectedItem}
                        />
                        {/* base info */}
                        {selectedItem == "linkto_profile" && (
                          <Box
                            sx={{
                              pt: 1,
                              pb: 1,
                              width: "100%",
                            }}
                            className="bank_profile_info"
                            id="bank_edit_profile_info"
                          >
                            {admin ? (
                              <HmTextInput
                                source="sBankName"
                                label="Institution Name"
                                validate={[required()]}
                                readonly={true}
                              />
                            ) : (
                              <TextInput
                                source="sBankName"
                                label="Institution Name"
                                sx={{
                                  width: "100%",
                                  "& input.Mui-disabled": {
                                    // backgroundColor: "#ddd",
                                    // borderRadius: "10px",
                                    color: "#00000061",
                                  },
                                }}
                                helperText={false}
                                variant="outlined"
                                // disabled
                                readOnly
                              />
                            )}

                            <SelectInput
                              source="sOrganizationType"
                              label="Type of Organization"
                              defaultValue={record?.sOrganizationType}
                              choices={[
                                { id: "Bank", name: "Bank" },
                                { id: "Credit Union", name: "Credit Union" },
                                { id: "CDFI", name: "CDFI" },
                                { id: "Other", name: "Other" },
                              ]}
                              fullWidth
                              variant="outlined"
                              helperText={false}
                              readOnly
                            />

                            <SelectInput
                              source="sBussinessType"
                              label="Business Type"
                              defaultValue={record?.sBussinessType}
                              choices={[
                                {
                                  id: "soleProprietorship",
                                  name: "Sole proprietorships/Unincorporated association/Trust",
                                },
                                {
                                  id: "corporation",
                                  name: "Corporation/Publicly traded corporations/Non-Profits",
                                },
                                { id: "llc", name: "LLCs" },
                                {
                                  id: "partnership",
                                  name: "Partnerships,LP's,LLP's",
                                },
                              ]}
                              fullWidth
                              variant="outlined"
                              helperText={false}
                              readOnly
                            />
                            <SelectInput
                              source="sBussinessClassification"
                              label="Industry"
                              defaultValue={record?.sBussinessClassification}
                              choices={[
                                {
                                  id: "Credit union",
                                  name: "Credit union",
                                },
                                {
                                  id: "Mortgage brokers and dealers",
                                  name: "Mortgage brokers and dealers",
                                },
                                {
                                  id: "Consumer lending",
                                  name: "Consumer lending",
                                },
                                {
                                  id: "Commercial Banking",
                                  name: "Commercial Banking",
                                },
                              ]}
                              fullWidth
                              variant="outlined"
                              helperText={false}
                              readOnly
                            />
                            <HmTextInput
                              source="sEin"
                              label="Institution EIN"
                              validate={[required()]}
                              className="numericText"
                              readOnly
                            />

                            {/* sStreet, sStreet2, sCity, sState, sZip */}
                            {/* <CustomAddress record={record} /> */}
                            <Grid
                              container
                              spacing={2}
                              sx={{
                                mt: "0px",
                                pt: "0px",
                                "& .MuiGrid-item": { mt: "0px", pt: "0px" },
                              }}
                            >
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                sx={{ mt: "0px", pt: "0px" }}
                              >
                                <HmTextInput
                                  source="sStreet"
                                  // name="street"
                                  label="Street"
                                  validate={[required()]}
                                  readOnly
                                />
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                sx={{ mt: "0px", pt: "0px" }}
                              >
                                <HmTextInput
                                  source="sStreet2"
                                  label="Street 2"
                                  readOnly
                                />
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                sx={{ mt: "0px", pt: "0px" }}
                              >
                                <HmTextInput
                                  source="sCity"
                                  label="City"
                                  validate={[required()]}
                                  readOnly
                                />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <HmTextInput
                                  source="sState"
                                  label="State"
                                  validate={[required()]}
                                  readOnly
                                />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <HmTextInput
                                  source="sZip"
                                  label="Zip Code"
                                  validate={[required()]}
                                  className="numericText"
                                  readOnly
                                />
                              </Grid>
                            </Grid>
                            {/* Institution Website */}

                            <HmTextInput
                              source="sUrl"
                              label="Institution Website"
                              validate={[required()]}
                              format={(value) => {
                                if (value && !value.startsWith("http")) {
                                  return `http://${value}`;
                                }
                                return value;
                              }}
                              parse={(value) => {
                                if (value && !value.startsWith("http")) {
                                  return `http://${value}`;
                                }
                                return value;
                              }}
                              readOnly
                            />
                          </Box>
                        )}
                        {/* contact */}
                        {selectedItem == "linkto_contact" && (
                          <Box
                            sx={{
                              pt: 2,
                              pb: 2,
                              width: "100%",
                              maxWidth: "500px",
                            }}
                            className="contact_info"
                            id="bank_edit_contact_info"
                          >
                            {/* Contact Phone Number  (xxx) xxx-xxxx */}
                            <HmTextInput
                              source="sBankPhone"
                              label="Contact Phone Number"
                              validate={[required()]}
                              format={formatPhone}
                              parse={parsePhone}
                              className="numericText"
                            />
                            <HmTextInput
                              source="sBankSupport"
                              label="Contact Email Address"
                              validate={[required()]}
                              // format={(value) => {
                              //   if (value && !value.startsWith("http")) {
                              //     return `http://${value}`;
                              //   }
                              //   return value;
                              // }}
                              // parse={(value) => {
                              //   if (value && !value.startsWith("http")) {
                              //     return `http://${value}`;
                              //   }
                              //   return value;
                              // }}
                            />
                            {/* contactname */}
                            <HmTextInput
                              source="sBankContactName"
                              label="Contact Name"
                              validate={[required()]}
                            />
                            {/* textMessageNumber */}
                            <HmTextInput
                              source="textMessageNumber"
                              label="Text Message Number"
                              format={(value) => {
                                if (value && value.length === 10) {
                                  return `(${value.slice(0, 3)}) ${value.slice(
                                    3,
                                    6
                                  )}-${value.slice(6)}`;
                                }
                                return value;
                              }}
                              parse={(value) => {
                                if (value && value.length === 14) {
                                  return value.replace(/\D/g, "");
                                }
                                return value;
                              }}
                            />
                            {/* Text Message Instruction */}
                            <HmTextInput
                              source="textMessageInstruction"
                              label="Text Message Instruction"
                            />
                          </Box>
                        )}
                        {/* E-Signature Sender */}
                        {selectedItem == "linkto_esign" && (
                          <Box
                            sx={{
                              pt: 2,
                              pb: 2,
                              width: "100%",
                              maxWidth: "500px",
                            }}
                            className="esignature_info"
                            id="bank_edit_esignature_info"
                          >
                            {/*esign_firstname  */}
                            <HmTextInput
                              source="esign_firstname"
                              label="E-Signature Sender First Name"
                              validate={[required()]}
                            />
                            {/*esign_lastname */}
                            <HmTextInput
                              source="esign_lastname"
                              label="E-Signature Sender Last Name"
                              validate={[required()]}
                            />
                            {/* esign_email */}
                            <HmTextInput
                              source="esign_email"
                              label="E-Signature Sender Email"
                              validate={[required()]}
                              //添加一个icon, change new email
                              sx={{
                                width: "100%",
                                "& input.Mui-readOnly": {
                                  backgroundColor: "#ddd",
                                  borderRadius: "10px",
                                },
                              }}
                              InputProps={{
                                readOnly: editEsignEmail ? false : true,
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {editEsignEmail ? (
                                      <></>
                                    ) : (
                                      <IconButton
                                        aria-label={"Change E-Signature Email"}
                                        onClick={() => setEditEsignEmail(true)}
                                        edge="end"
                                        sx={{ marginLeft: "0px" }}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    )}
                                  </InputAdornment>
                                ),
                              }}
                            />
                            <ShowEsignChangeEmailMessage />
                          </Box>
                        )}
                        {/* Logos */}
                        {selectedItem == "linkto_logos" && (
                          <Box
                            sx={{
                              pt: 2,
                              pb: 2,
                              width: "100%",
                              maxWidth: "800px",
                            }}
                            className="logos_info"
                            id="bank_edit_logos_info"
                          >
                            <ShowLogos />
                            <ShowLogos size="small" />
                          </Box>
                        )}
                        {/* Welcome Page */}
                        {selectedItem == "linkto_welcome" && (
                          <Box
                            sx={{
                              pt: 2,
                              pb: 2,
                              width: "100%",
                              maxWidth: "800px",
                            }}
                            className="welcome_page_info"
                            id="bank_edit_welcome_page_info"
                          >
                            {/* welcome_page */}
                            {/* Consumer Portal URL for Sign-Up , URL with a copy link */}
                            <CustomLinkField
                              source="applicationUrl"
                              label="Consumer Portal URL for Sign-Up"
                              // link={record?.applicationUrl}
                            />
                            {/* Consumer Portal URL for Log-In */}
                            <CustomLinkField
                              source="loginUrl"
                              label="Consumer Portal URL for Log-In"
                              // link={record?.loginUrl}
                            />
                            {/* Business URL for Sign-Up */}
                            <CustomLinkField
                              source="businessSignUrl"
                              label="Business URL for Sign-Up"
                              // link={record?.businessSignUrl}
                            />
                            {/* Business URL for Log-In */}
                            <CustomLinkField
                              source="bussinessLoginUrl"
                              label="Business URL for Log-In"
                              // link={record?.bussinessLoginUrl}
                            />
                            {/* Welcome Headline */}
                            <HmTextInput
                              source="sSlogan"
                              label="Welcome Headline"
                              validate={[required()]}
                            />
                            {/* Welcome Message */}
                            <HmTextInput
                              source="sWelcomeMsg"
                              label="Welcome Message"
                              sx={{
                                "& .MuiInputBase-root": {
                                  padding: 0,
                                },
                              }}
                              validate={[required()]}
                              multiline
                              rows={4}
                              maxRows={4}
                            />
                          </Box>
                        )}
                        {/* Language Settings */}
                        {selectedItem == "linkto_language" && (
                          <Box
                            sx={{
                              pt: 2,
                              pb: 2,
                              width: "100%",
                              maxWidth: "800px",
                            }}
                            className="language_settings_info"
                            id="bank_edit_language_settings_info"
                          >
                            <CustomLanguageSettings />
                          </Box>
                        )}
                        {/* Probation */}
                        {selectedItem == "linkto_probation" && (
                          <Box
                            sx={{
                              pt: 2,
                              pb: 2,
                              width: "100%",
                              maxWidth: "800px",
                            }}
                            className="probation_info"
                            id="bank_edit_probation_info"
                          >
                            <ProbationDateField />
                          </Box>
                        )}
                      </Box>
                    </SectionStyled>
                  </SimpleForm>
                </Box>
              </Box>
            </SimpleShowLayout>
          </Show>
        </div>
      </div>
    </>
  );
};

export default BankShow;
